import React, { useState, useEffect } from 'react';
import {
  Button,
  message,
  Tag,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  WalletOutlined,
  LeftOutlined,
  RightOutlined,
  DownOutlined,
  FilterOutlined,
  GiftOutlined,
  CloseOutlined,
  LinkOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/vi';
import { NotiModal } from '../../../components/Noti/NotiModal';
import { IncomeTransactionModalMobile } from '../../../components/income/IncomeTransactionModalMobile';
import { TransactionDetailModalMobile } from '../../../components/income/TransactionDetailModalMobile';
import { FilterModalMobile } from '../../../components/income/FilterModalMobile';
import {
  IncomeTransaction,
  IncomeSource,
  SourceWallet,
  incomeService,
  formatCurrency,
} from '../../../services/incomeService';
import styles from './IncomePageMobile.module.css';

// Cấu hình locale tiếng Việt
dayjs.locale('vi');

export const IncomePageMobile: React.FC = () => {
  const [transactions, setTransactions] = useState<IncomeTransaction[]>([]);
  const [sources, setSources] = useState<IncomeSource[]>([]);
  const [wallets, setWallets] = useState<SourceWallet[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<IncomeTransaction | undefined>();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<number | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [selectedTransaction, setSelectedTransaction] = useState<IncomeTransaction | undefined>();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    source_id: undefined as number | undefined,
    wallet_id: undefined as number | undefined,
    transaction_type: undefined as 'income' | undefined,
    search: '',
    start_date: undefined as string | undefined,
    end_date: undefined as string | undefined,
  });
  const [showSourceFilter, setShowSourceFilter] = useState(false);
  const [showWalletFilter, setShowWalletFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadTransactions = async (reset = false) => {
    try {
      // Xác định trang cần load
      const pageToLoad = reset ? 1 : currentPage;
      
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const apiParams: any = {
        page: pageToLoad,
        limit: pageSize,
        source_id: filters.source_id,
        wallet_id: filters.wallet_id,
        transaction_type: filters.transaction_type,
        search: filters.search,
      };

      // Sử dụng start_date và end_date filter nếu có, nếu không thì dùng currentMonth
      if (filters.start_date || filters.end_date) {
        if (filters.start_date) {
          apiParams.start_date = filters.start_date;
        } else {
          apiParams.start_date = currentMonth.startOf('month').format('YYYY-MM-DD');
        }
        if (filters.end_date) {
          apiParams.end_date = filters.end_date;
        } else {
          apiParams.end_date = currentMonth.endOf('month').format('YYYY-MM-DD');
        }
      } else {
        apiParams.start_date = currentMonth.startOf('month').format('YYYY-MM-DD');
        apiParams.end_date = currentMonth.endOf('month').format('YYYY-MM-DD');
      }

      console.log('API Parameters:', apiParams);

      const response = await incomeService.getTransactions(apiParams);
      
      console.log('API Response:', response);
      console.log('Transactions:', response.data);
      console.log('Pagination:', response.pagination);
      
      // Cập nhật dữ liệu
      if (reset) {
        setTransactions(response.data);
        setCurrentPage(1);
      } else {
        // Merge data mới với data cũ, tránh trùng lặp
        setTransactions(prev => {
          const existingIds = new Set(prev.map(transaction => transaction.id));
          const newTransactions = response.data.filter(transaction => !existingIds.has(transaction.id));
          return [...prev, ...newTransactions];
        });
      }
      
      // Cập nhật pagination info
      const pagination = response.pagination;
      if (pagination) {
        setTotal(pagination.total);
        setHasMore(pageToLoad < pagination.totalPages);
        console.log('Has more:', pageToLoad < pagination.totalPages, 'Current page:', pageToLoad, 'Total pages:', pagination.totalPages);
      } else {
        setHasMore(response.data.length === pageSize);
        console.log('No pagination, has more based on data length:', response.data.length === pageSize);
      }
    } catch (error) {
      console.error('Error in loadTransactions:', error);
      message.error('Không thể tải danh sách giao dịch');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more function
  const loadMore = () => {
    console.log('LoadMore called - loadingMore:', loadingMore, 'hasMore:', hasMore, 'loading:', loading);
    if (!loadingMore && hasMore && !loading) {
      console.log('Incrementing current page from', currentPage, 'to', currentPage + 1);
      setCurrentPage(prev => prev + 1);
    } else {
      console.log('LoadMore blocked - loadingMore:', loadingMore, 'hasMore:', hasMore, 'loading:', loading);
    }
  };

  const loadSources = async () => {
    try {
      const sourcesData = await incomeService.getSources();
      setSources(sourcesData);
    } catch (error) {
      message.error('Không thể tải nguồn thu nhập');
    }
  };

  const loadWallets = async () => {
    try {
      const walletsData = await incomeService.getWallets();
      setWallets(walletsData);
    } catch (error) {
      message.error('Không thể tải danh sách ví');
    }
  };

  // Effect để load dữ liệu khi page thay đổi
  useEffect(() => {
    console.log('useEffect triggered - currentPage:', currentPage, 'pageSize:', pageSize);
    if (currentPage === 1) {
      loadTransactions(true); // Reset data khi về trang 1
    } else {
      loadTransactions(false); // Load more khi trang > 1
    }
  }, [currentPage, pageSize]);

  // Effect để reset pagination khi filter thay đổi
  useEffect(() => {
    console.log('Filter effect triggered - filters:', filters, 'currentMonth:', currentMonth);
    setCurrentPage(1);
    setHasMore(true);
    setTransactions([]);
    setTotal(0);
    // Fetch dữ liệu mới với filter mới
    setTimeout(() => {
      loadTransactions(true);
    }, 100);
  }, [filters, currentMonth]);

  useEffect(() => {
    loadSources();
    loadWallets();
  }, []);

  const handleAddTransaction = () => {
    setEditingTransaction(undefined);
    setIsModalOpen(true);
  };

  const handleEditTransaction = (record: IncomeTransaction) => {
    setEditingTransaction(record);
    setIsModalOpen(true);
    setIsDetailModalOpen(false);
  };

  const handleDeleteTransaction = async (id: number) => {
    try {
      await incomeService.deleteTransaction(id);
      message.success('Xóa giao dịch thành công');
      // Reload dữ liệu để cập nhật số dư ví
      loadTransactions(true);
      // Reload danh sách ví để cập nhật số dư
      loadWallets();
      setIsDetailModalOpen(false);
    } catch (error: any) {
      // Hiển thị thông báo chi tiết từ API
      const errorMessage = error.response?.data?.error || error.message || 'Có lỗi xảy ra khi xóa giao dịch';
      message.error(errorMessage);
    }
  };

  const handleShowDeleteModal = (transactionId: number) => {
    setTransactionToDelete(transactionId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (transactionToDelete) {
      await handleDeleteTransaction(transactionToDelete);
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    }
  };

  const handleModalSuccess = () => {
    loadTransactions(true);
    loadWallets(); // Reload danh sách ví để cập nhật số dư
  };

  const getSourceById = (id: number) => {
    return sources.find(s => s.id === id);
  };

  const getWalletById = (id: number) => {
    return wallets.find(w => w.id === id);
  };

  const getIconContainerClass = (transactionType: 'income') => {
    return styles.iconContainerIncome;
  };

  const getSourceTagClass = (transactionType: 'income') => {
    return styles.incomeTag;
  };

  const getAmountClass = (transactionType: 'income') => {
    return styles.amountIncome;
  };

  const getTransactionIcon = (transactionType: 'income') => {
    return <ArrowUpOutlined />;
  };

  const getTransactionTypeText = (transactionType: 'income') => {
    return 'Thu nhập';
  };

  // Nhóm giao dịch theo ngày
  const groupTransactionsByDate = () => {
    const groups: { [key: string]: IncomeTransaction[] } = {};
    
    // Sort transactions by income_date DESC to maintain order
    const sortedTransactions = [...transactions].sort((a, b) => 
      new Date(b.income_date).getTime() - new Date(a.income_date).getTime()
    );
    
    sortedTransactions.forEach(transaction => {
      const date = dayjs(transaction.income_date).format('DD/MM/YYYY');
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(transaction);
    });

    return Object.entries(groups).sort(([a], [b]) => 
      dayjs(b, 'DD/MM/YYYY').diff(dayjs(a, 'DD/MM/YYYY'))
    );
  };

  const handleTransactionClick = (transaction: IncomeTransaction) => {
    setSelectedTransaction(transaction);
    setIsDetailModalOpen(true);
  };

  // Throttle scroll để tránh gọi quá nhiều
  const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    
    console.log('Scroll percentage:', scrollPercentage, 'Has more:', hasMore, 'Loading more:', loadingMore);
    
    // Trigger load more khi scroll đến 80%
    if (scrollPercentage > 0.8 && hasMore && !loadingMore && !loading) {
      console.log('Triggering load more...');
      loadMore();
    }
  }, [hasMore, loadingMore, loading]);

  const renderTransactionCard = (transaction: IncomeTransaction) => {
    const source = getSourceById(transaction.source_id);
    const wallet = getWalletById(transaction.wallet_id);
    const sourceName = source?.name || 'Không xác định';
    const walletName = wallet?.name || 'Không xác định';
    
    return (
      <div 
        key={transaction.id} 
        className={styles.transactionCard}
        onClick={() => handleTransactionClick(transaction)}
        style={{ cursor: 'pointer' }}
      >
        <div className={`${styles.iconContainer} ${getIconContainerClass(transaction.transaction_type)}`}>
          {getTransactionIcon(transaction.transaction_type)}
        </div>
        
        <div className={styles.transactionDetails}>
          <p className={styles.transactionTitle}>
            {transaction.notes || `${getTransactionTypeText(transaction.transaction_type)} từ ${sourceName}`}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span className={`${styles.source} ${getSourceTagClass(transaction.transaction_type)}`}>
              {source?.icon || '💰'} {sourceName}
            </span>
            <span className={`${styles.source} ${getSourceTagClass(transaction.transaction_type)}`}>
              <WalletOutlined /> {walletName}
            </span>
          </div>
        </div>
        
        <p className={`${styles.amount} ${getAmountClass(transaction.transaction_type)}`}>
          {transaction.transaction_type === 'income' ? '+' : '-'}{formatCurrency(transaction.amount)}
        </p>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIcon}>
        <DollarOutlined />
      </div>
      <div className={styles.emptyText}>
        Chưa có giao dịch thu nhập nào
      </div>
      <div className={styles.emptySubtext}>
        Bắt đầu thêm giao dịch đầu tiên của bạn
      </div>
    </div>
  );

  const handlePreviousMonth = () => {
    setCurrentMonth(prev => prev.subtract(1, 'month'));
    setCurrentPage(1); // Reset về trang đầu khi đổi tháng
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => prev.add(1, 'month'));
    setCurrentPage(1); // Reset về trang đầu khi đổi tháng
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters(prev => ({ 
      ...prev, 
      [key]: value 
    }));
    setCurrentPage(1); // Reset về trang đầu khi thay đổi filter
  };

  const handleListHeaderClick = () => {
    setShowSourceFilter(!showSourceFilter);
  };

  const handleSourceSelect = (sourceId: number | undefined) => {
    handleFilterChange('source_id', sourceId);
    setShowSourceFilter(false);
  };

  const getSelectedSourceName = () => {
    if (!filters.source_id) return 'Tất cả nguồn';
    const source = sources.find(s => s.id === filters.source_id);
    return source?.name || 'Tất cả nguồn';
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleFilterApply = (newFilters: any) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset về trang đầu khi áp dụng filter
  };

  const getFilterCount = () => {
    let count = 0;
    if (filters.source_id) count++;
    if (filters.wallet_id) count++;
    if (filters.transaction_type) count++;
    if (filters.start_date) count++;
    if (filters.end_date) count++;
    if (filters.search) count++;
    return count;
  };

  const handleDetailModalEdit = () => {
    if (selectedTransaction) {
      handleEditTransaction(selectedTransaction);
      // Reload wallets để cập nhật số dư sau khi edit
      loadWallets();
    }
  };

  const handleDetailModalDelete = () => {
    if (selectedTransaction) {
      handleShowDeleteModal(selectedTransaction.id);
      setIsDetailModalOpen(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.mobileShell}>
        {/* App Header */}
        <header className={styles.appHeader}>
          <div className={styles.dateSelector}>
            <LeftOutlined onClick={handlePreviousMonth} />
            <span>{currentMonth.format('MMMM YYYY')}</span>
            <RightOutlined onClick={handleNextMonth} />
          </div>
        </header>

        {/* Transaction List */}
        <main className={styles.transactionList}>
          <div className={styles.listHeader}>
            <div className={styles.headerLeft} onClick={handleListHeaderClick}>
              <h3>{getSelectedSourceName()}</h3>
              <DownOutlined style={{ 
                transform: showSourceFilter ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            </div>
            <div className={styles.headerRight}>
              <div className={styles.addButton} onClick={handleAddTransaction}>
                <PlusOutlined />
              </div>
              <div className={styles.filterButton} onClick={handleFilterModalOpen}>
                <FilterOutlined />
                {getFilterCount() > 0 && (
                  <span className={styles.filterBadge}>{getFilterCount()}</span>
                )}
              </div>
            </div>
          </div>

          {/* Source Filter Dropdown */}
          {showSourceFilter && (
            <div className={styles.sourceFilterDropdown}>
              <div 
                className={styles.sourceFilterOption}
                onClick={() => handleSourceSelect(undefined)}
              >
                <span>Tất cả nguồn</span>
              </div>
              {sources.map((source) => (
                <div 
                  key={source.id}
                  className={styles.sourceFilterOption}
                  onClick={() => handleSourceSelect(source.id)}
                >
                  <span>{source.icon} {source.name}</span>
                </div>
              ))}
            </div>
          )}

          {/* Scrollable Transaction Content */}
          <div className={styles.transactionContent} onScroll={handleScroll}>
            {transactions.length > 0 ? (
              <>
                {groupTransactionsByDate().map(([date, transactions]) => (
                  <div key={date} className={styles.transactionGroup}>
                    <p className={styles.groupDate}>{date}</p>
                    {transactions.map(renderTransactionCard)}
                  </div>
                ))}
                {loadingMore && (
                  <div className={styles.loadingMore}>
                    <div className={styles.loadingSpinner}></div>
                    <span>Đang tải thêm...</span>
                  </div>
                )}
                {!hasMore && transactions.length > 0 && (
                  <div className={styles.noMoreData}>
                    <span>Đã tải hết dữ liệu ({transactions.length}/{total} mục)</span>
                  </div>
                )}
              </>
            ) : (
              renderEmptyState()
            )}
          </div>
        </main>
      </div>

      {/* Modal thêm/sửa giao dịch */}
      <IncomeTransactionModalMobile
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        transaction={editingTransaction}
        onSuccess={handleModalSuccess}
      />

      {/* Modal chi tiết giao dịch */}
      <TransactionDetailModalMobile
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        transaction={selectedTransaction}
        source={selectedTransaction ? getSourceById(selectedTransaction.source_id) : undefined}
        wallet={selectedTransaction ? getWalletById(selectedTransaction.wallet_id) : undefined}
        onEdit={handleDetailModalEdit}
        onDelete={handleDetailModalDelete}
        onReloadWallets={loadWallets}
      />

      {/* Modal filter */}
      <FilterModalMobile
        isOpen={isFilterModalOpen}
        onClose={handleFilterModalClose}
        onApply={handleFilterApply}
        currentFilters={filters}
        sources={sources}
        wallets={wallets}
      />

      {/* Modal xác nhận xóa */}
      <NotiModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        title="Xác nhận xóa"
        message="Bạn có chắc chắn muốn xóa giao dịch này?"
        confirmText="Xóa"
        cancelText="Hủy"
      />
    </div>
  );
};
